/**
 * CookieBanner — Bandeau de consentement cookies conforme CNIL / ePrivacy.
 *
 * PsyGestion n'utilise que des cookies techniques strictement nécessaires
 * (authentification JWT, préférences UI). Aucun cookie publicitaire ni traceur tiers.
 *
 * Conformément à l'article 82 de la loi Informatique et Libertés, les cookies
 * strictement nécessaires sont exemptés de consentement. Ce bandeau informe
 * l'utilisateur de manière transparente sans bloquer l'accès au service.
 */

import React, { useState, useEffect } from "react";

const STORAGE_KEY = "psygestion_cookie_consent";

type ConsentState = "accepted" | "refused" | null;

const C = {
  navy: "#1B3A5C",
  teal: "#2A9D8F",
  muted: "#6B85A3",
  border: "#D6E4F0",
  white: "#FFFFFF",
  bg: "#F7FAFE",
};

export default function CookieBanner() {
  const [consent, setConsent] = useState<ConsentState>(null);
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ConsentState | null;
    if (!stored) {
      // Délai court pour ne pas gêner le chargement de la page
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    setConsent(stored);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    setConsent("accepted");
    setVisible(false);
  };

  const handleRefuse = () => {
    localStorage.setItem(STORAGE_KEY, "refused");
    setConsent("refused");
    setVisible(false);
  };

  if (!visible || consent !== null) return null;

  return (
    <div
      role="dialog"
      aria-label="Gestion des cookies"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: C.white,
        borderTop: `1px solid ${C.border}`,
        boxShadow: "0 -4px 24px rgba(27,58,92,.1)",
        padding: "16px 20px",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        {/* Icône + texte */}
        <div style={{ flex: 1, minWidth: 240 }}>
          <p style={{ margin: 0, fontSize: 14, color: C.navy, fontWeight: 600, marginBottom: 4 }}>
            🍪 Cookies & Confidentialité
          </p>
          <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
            PsyGestion utilise uniquement des cookies techniques indispensables à votre authentification
            et à la mémorisation de vos préférences. Aucun traceur publicitaire ni partage avec des tiers.{" "}
            <button
              onClick={() => setDetail((d) => !d)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: C.teal,
                cursor: "pointer",
                fontSize: 13,
                textDecoration: "underline",
              }}
            >
              {detail ? "Masquer les détails" : "En savoir plus"}
            </button>
          </p>

          {detail && (
            <div
              style={{
                marginTop: 10,
                backgroundColor: C.bg,
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.7,
              }}
            >
              <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.navy }}>Cookies utilisés :</p>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                <li>
                  <strong>sb-auth-token</strong> — Session Supabase (authentification). Durée : 1 heure,
                  renouvelé automatiquement. Strictement nécessaire.
                </li>
                <li>
                  <strong>psygestion_*</strong> — Préférences d'interface (mode navigation, simulateur).
                  Stocké en localStorage. Strictement nécessaire.
                </li>
              </ul>
              <p style={{ margin: "6px 0 0" }}>
                Aucun cookie Google Analytics, Facebook Pixel, Hotjar ou autre traceur tiers n'est présent.{" "}
                <a href="/confidentialite" style={{ color: C.teal }}>Politique de confidentialité complète →</a>
              </p>
            </div>
          )}
        </div>

        {/* Boutons */}
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={handleRefuse}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: C.white,
              color: C.muted,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Continuer sans accepter
          </button>
          <button
            onClick={handleAccept}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: "none",
              background: C.teal,
              color: C.white,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            J'accepte
          </button>
        </div>
      </div>
    </div>
  );
}
